// storage-bucket-table.tsx — Per-bucket breakdown table for the Storage tab.
// Exported: StorageBucketTable component + formatGiB helper (GiB = 1024³ binary basis).
import { Database } from 'lucide-react';
import { Card, CardBody, CardHeader, CardTitle, Skeleton } from '@vnetwork/ui/primitives';
import { DataTable, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import type { OpsStorageBucketRow } from '@/api/operations';

/* ── GiB formatter — 1024³ binary basis ──────────────────────────────────── */

/**
 * Format storage bytes in binary (1024-based) units.
 * Storage at-rest = GiB (1024³), NOT GB (1000³).
 * Mirrors labs/@partner/src/pages/services/oss/storage-format.ts formatGiB.
 */
export function formatGiB(n: number | null | undefined): string {
  const v = Number(n ?? 0);
  if (!Number.isFinite(v) || v <= 0) return '0 B';
  const k = 1024;
  const units = ['B', 'KiB', 'MiB', 'GiB', 'TiB', 'PiB'];
  const i = Math.min(units.length - 1, Math.floor(Math.log(v) / Math.log(k)));
  const dp = i >= 3 ? 2 : i >= 1 ? 1 : 0;
  return `${(v / Math.pow(k, i)).toFixed(dp)} ${units[i]}`;
}

/* ── Column definitions ───────────────────────────────────────────────────── */

const bucketColumns: ColumnDef<OpsStorageBucketRow, unknown>[] = [
  {
    id: 'bucket',
    accessorKey: 'bucket',
    header: 'Bucket',
    cell: ({ row }) => (
      <span style={{ fontWeight: 500, fontFamily: 'monospace', fontSize: 'var(--vn-font-size-xs)' }}>
        {row.original.bucket}
      </span>
    ),
  },
  {
    id: 'peak_stored_bytes',
    header: 'Peak (billed)',
    cell: ({ row }) => formatGiB(row.original.peak_stored_bytes),
  },
  {
    id: 'avg_stored_bytes',
    header: 'Average',
    cell: ({ row }) => formatGiB(row.original.avg_stored_bytes),
  },
  {
    id: 'gib_month',
    header: 'GiB-month',
    cell: ({ row }) =>
      row.original.gib_month != null ? `${row.original.gib_month.toFixed(2)} GiB` : '—',
  },
];

/* ── Props ────────────────────────────────────────────────────────────────── */

interface StorageBucketTableProps {
  buckets: OpsStorageBucketRow[];
  isLoading: boolean;
}

/* ── Component ───────────────────────────────────────────────────────────── */

export function StorageBucketTable({ buckets, isLoading }: StorageBucketTableProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Per-bucket breakdown</CardTitle>
      </CardHeader>
      <CardBody style={{ padding: 0 }}>
        {isLoading ? (
          <div style={{ padding: 'var(--vn-space-4)', display: 'grid', gap: 'var(--vn-space-2)' }}>
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} style={{ height: 24 }} />
            ))}
          </div>
        ) : (
          <DataTable
            columns={bucketColumns}
            data={buckets}
            getRowId={(r) => r.bucket}
            dense
            emptyState={
              <EmptyState
                icon={<Database size={24} />}
                title="No bucket data"
                description="No per-bucket data available for this window (non-Wasabi services show no data)."
              />
            }
            footer={
              buckets.length > 0
                ? `${buckets.length} bucket${buckets.length !== 1 ? 's' : ''} · storage = GiB (1024³)`
                : undefined
            }
          />
        )}
      </CardBody>
    </Card>
  );
}
